import { ArrowRightIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { ExclamationCircleIcon } from '@heroicons/react/24/solid';
import React, { type FormEvent, useState } from 'react';
import EasyVoteLogo from 'shared/src/components/EasyVoteLogo';
import Spinner from 'shared/src/components/Spinner';
import { Alert, AlertDescription, AlertTitle, Button, Input, Label } from 'shared/src/components/ui/new';
import { cn } from 'shared/src/utils';

export interface ForgotPasswordProps {
  startPasswordReset: (username: string) => Promise<boolean>;
  onSuccessfulReset: (username: string) => void;
  className?: string;
  framed?: boolean;
}

export default function ForgotPassword({ startPasswordReset, onSuccessfulReset, className, framed = false }: ForgotPasswordProps) {
  const [username, setUsername] = useState<string>();
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  function handleSubmit(event: FormEvent) {
    event.preventDefault();

    if (!username) return;

    setLoading(true);
    setErrorMessage(null);

    startPasswordReset(username).then((success) => {
      if (success) {
        onSuccessfulReset(username);
      } else {
        setErrorMessage("We weren't able to start a password reset for that User ID.");
        setLoading(false);
      }
    });
  }

  return (
    <div className={cn('theme-ev flex min-h-full flex-col justify-center py-12 xs:px-6 lg:px-8', className)}>
      <div className="sm:mx-auto sm:w-full sm:max-w-md text-center">
        <EasyVoteLogo className="mx-auto h-14 w-auto" />
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className={cn(framed && 'bg-white p-6 shadow sm:rounded-lg sm:p-8')}>
          <h2 className="mb-4 text-lg font-semibold text-foreground">Forgot password</h2>
          {errorMessage ? (
            <Alert className="mb-6" variant="destructive">
              <ExclamationCircleIcon className="size-5" />
              <AlertTitle>Reset unavailable</AlertTitle>
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
          ) : (
            <Alert className="mb-6" variant="default">
              <InformationCircleIcon className="size-4" />
              <AlertDescription>
                <p>
                  If you've entered a valid User ID, a reset code will be sent via <strong>text</strong> or <strong>email</strong> based on your contact settings.
                </p>
                <ul className="list-disc my-1 space-y-1 pl-5">
                  <li>
                    <strong>Text</strong> if the phone number is valid and authorized
                  </li>
                  <li>
                    <strong>Email</strong> if your phone number isn't applicable
                  </li>
                </ul>
                <p>The code and a new password can be entered on the next page.</p>
              </AlertDescription>
            </Alert>
          )}
          <form onSubmit={handleSubmit} className="space-y-6" method="POST">
            <div>
              <Label htmlFor="username">EasyVote User ID</Label>
              <div className="mt-2">
                <Input
                  id="username"
                  name="username"
                  type="text"
                  autoFocus={true}
                  placeholder="Your EasyVote User ID"
                  required
                  onChange={({ target: { value } }) => setUsername(value)}
                />
              </div>
            </div>

            <Button type="submit" disabled={loading} className="w-full flex items-center space-x-2">
              <Spinner show={loading} light />
              <span>Next</span>
              <ArrowRightIcon />
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
